import qs from "qs";
import slugify from "slugify";
import config from "../config";
import fileFormats from "../data/format.json";
import compressFileFormats from "../data/compressFormat.json";
import categoriesFormats from "../data/formatCategories.json";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

export const getDisplayedName = (filename, width) => {
    let newFilename = filename.split('.');
    if (newFilename.length > 2) {
        const lastValue = newFilename.pop();
        const firstValue = newFilename.join('.');
        newFilename = [firstValue, lastValue]
        // before : filename-really-long.1234.1234.1234.1234.1234.png => fil...ong.1234.1234.1234.1234.1234.png
        // after  : filename-really-long.1234.1234.1234.1234.1234.png => fil...234.png
    }
    if (width && width < 768) {
        if (newFilename[0].length > 18) {
            newFilename[0] = `${newFilename[0].substring(0, 11)}...${newFilename[0].substring(newFilename[0].length - 3)}`
        }
    } else {
        if (newFilename[0].length > 10) {
            newFilename[0] = `${newFilename[0].substring(0, 3)}...${newFilename[0].substring(newFilename[0].length - 3)}`
        }
    }
    return newFilename.join('.');
}

const handleResponse = response => {
    if (!response.headers.get("content-type") || response.headers.get("content-type").indexOf('application/json') === -1) {
        if (response.ok) return response.statusText;
        throw new Error(`${response.status}: ${response.statusText}`);
    }
    if (response.ok) return response.json();
    return response.json()
        .then(json => {
            throw new Error(json.message);
        })
}

/**
 *
 * @param {File} file
 * @returns {Promise<Object>} Link of the uploaded file
 */
export async function localUpload(file, retry = 0) {
    try {
        const fileName = `tmp/${Math.round(Math.random() * 10000000)}/${slugify(file.name, { remove: /[^a-zA-Z0-9. -]/g, lower: true })}`;

        const response = await fetch(`${config.serviceConverter.url}/upload/url?fileName=${fileName}&fileType=${file.type}`)
        const json = await handleResponse(response)

        await fetch(json.signedRequest, { method: 'PUT', body: file })

        return { url: json.url }
    } catch (error) {
        if (retry > 20) throw error;
        return localUpload(file, retry + 1)
    }
}

export const multipartLocalUpload = async (file) => {
    // 5Mo min upload size
    const chunkSize = 1024 * 1024 * 5;
    const nbChunks = Math.ceil(file.size / chunkSize);
    const fileName = `tmp/${Math.round(Math.random() * 10000000)}/${slugify(file.name, { remove: /[^a-zA-Z0-9. -]/g })}`;
    const chunks = [];

    while (chunks.length < nbChunks) {
        const offset = chunks.length * chunkSize;
        const chunk = file.slice(offset, offset + chunkSize);
        chunks.push(chunk);
    }

    const response = await fetch(`${config.serviceConverter.url}/upload/url/multipart?fileName=${fileName}&fileType=${file.type}&parts=${nbChunks}`)
    const json = await response.json();

    await json.urls.reduce(async (promise, url, index) => {
        return promise.then(() => fetch(url.signedRequest, { method: 'PUT', body: chunks[index] }));
    }, Promise.resolve())

    return { fileName, uploadId: json.uploadId }
}

export const multipartLocalUploadValidate = async (fileName, uploadId) => {

    const response = await fetch(`${config.serviceConverter.url}/upload/url/multipart/validate?fileName=${fileName}&uploadId=${uploadId}`)
    const json = await response.json();

    return json;
}

export const cloudUpload = (file, customerId) => {
    const fileName = slugify(file.name, { remove: /[^a-zA-Z0-9. -]/g });

    return fetch(`${config.serviceConverter.url}/upload/cloud?fileName=${fileName}`, {
        headers: { Authorization: `Bearer ${customerId}` }
    })
        .then(handleResponse)
        .then(json => {
            return fetch(json.signedRequest, {
                method: 'PUT',
                body: file
            })
                .then(handleResponse)
                .then(() => fetch(`${config.serviceConverter.url}/cloud/`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${customerId}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: json.url })
                }))
                .then(handleResponse)
        })
}

export const googleDriveUpload = (docs, token) => {
    const ids = docs.map(doc => doc.id)

    return fetch(`${config.serviceConverter.url}/upload/google-drive?${qs.stringify({ id: ids })}`, {
        headers: { Authorization: `Bearer ${token}` }
    })
        .then(handleResponse)
}

export const convertFiles = (customerId, files) => fetch(`${config.serviceConverter.url}/convert`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${customerId}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ files, companyName: config.companyName })
})
    .then(handleResponse)

export const getAllowedTypes = () => Object.keys(fileFormats);
export const getCompressAllowedTypes = () => compressFileFormats;

export const getOutputTypes = (type, name) => {
    const result = fileFormats[type] ? fileFormats[type] : [];
    const resultClone = [...result];
    if (type === "image/jpeg" && !!name) { //could be avoided by changing the whole data/format.json
        if (name.toLowerCase().indexOf('.jpg') !== -1) {
            resultClone.push("jpeg")
        } else {
            resultClone.push("jpg")
        }
    }
    return resultClone;
}

const sortTypeIntoCategories = types => {
    const newFileFormat = {
        "Document": [],
        "Image": [],
        "Ebook": [],
        "Video": [],
        "Audio": [],
        "Police": [],
        "Archive": []
    }
    types.forEach(type => {
        const category = Object.keys(categoriesFormats).find(cat => categoriesFormats[cat].indexOf(type) !== -1) || "Document"
        newFileFormat[category].push(type);
    });
    Object.keys(newFileFormat).forEach(item => {
        if (newFileFormat[item].length === 0) {
            delete newFileFormat[item];
        }
    })
    return newFileFormat;
}

export const getOutputExtensions = (type, name) => {
    const output = getOutputTypes(type, name).sort(function (a, b) {
        if (a < b) { return -1; }
        if (a > b) { return 1; }
        return 0;
    })
    const categorizedInput = sortTypeIntoCategories([...output])
    return { mobile: output, desktop: categorizedInput };
}

export const deleteFile = (userId, fileUrl) => fetch(`${config.serviceConverter.url}/file?url=${encodeURI(fileUrl)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${userId}` },
})
    .then(handleResponse)

export const compressFiles = (userId, files) => fetch(`${config.serviceConverter.url}/compress`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${userId}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ files, companyName: config.companyName })
})
    .then(handleResponse)

export const splitFiles = (userId, files) => fetch(`${config.serviceConverter.url}/split`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${userId}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ files })
})
    .then(handleResponse)

export const sizeFormat = (bytes) => {
    const thresh = 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = ['kB', 'MB', 'GB'];
    let u = -1;
    const r = 10;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


    return bytes.toFixed(1) + ' ' + units[u];
}

export function getPdfFromLocalFile(file) {

    var fileReader = new FileReader();

    return new Promise(async (resolve, reject) => {

        fileReader.addEventListener('error', reject)

        fileReader.onload = async function () {
            const typedarray = new Uint8Array(this.result);
            const pdf = await pdfjs.getDocument(typedarray).promise;
            resolve(pdf);
        }

        fileReader.readAsArrayBuffer(file);
    })

}